import { usersMeQuery } from 'features/users/api/userQueries'
import { Map, MapPrivacy } from '../types'
import { queryClient } from 'queryClient'
import { User } from 'features/users/types'
import { CommonSharingScope } from 'features/common/types'
import { getMapQuery } from '../api/mapQueries'
import { accountUsersQuery } from 'features/account/api/accountQueries'
import { groupMembersQuery } from 'features/groups/api/groupsQueries'

export type MapPrivacyProps = Pick<Map, 'id' | 'ownerId' | 'privacy' | 'sharingScope' | 'users' | 'groups'>

export async function getMapAllowedUsers({
  id,
  privacy,
  sharingScope,
  users,
  groups,
  ownerId
}: MapPrivacyProps): Promise<User[]> {
  if (privacy === MapPrivacy.Private) {
    const currentUser = await queryClient.fetchQuery(usersMeQuery)
    return [currentUser]
  }

  // Note:
  // public map endpoint has broken sharingScope, so we nullify it in API level
  // now, if we need to get correct sharingScope, we need to fetch full map
  // @see https://app.shortcut.com/cartoteam/story/485846
  const needSharingScope = privacy !== MapPrivacy.Public
  if (needSharingScope && !sharingScope) {
    const fullMap = await queryClient.fetchQuery(getMapQuery(id))
    sharingScope = fullMap.sharingScope
    users = fullMap.users
    groups = fullMap.groups
  }

  const allUsers = (await queryClient.fetchQuery(accountUsersQuery)) as User[]
  if (sharingScope === CommonSharingScope.Organization || privacy === MapPrivacy.Public) {
    return allUsers
  }

  const result: User[] = []
  const mapOwner = allUsers.find((u) => u.user_id === ownerId)
  if (mapOwner) {
    result.push(mapOwner)
  }

  if (sharingScope === CommonSharingScope.Specific) {
    if (users && users.length > 0) {
      const allowedUsers = users.map((user) => allUsers.find((u) => u.user_id === user.id)).filter(Boolean) as User[]
      result.push(...allowedUsers)
    }

    if (groups && groups.length > 0) {
      await Promise.all(
        groups.map(async (group) => {
          const groupMembers = await queryClient.fetchQuery(groupMembersQuery(group.id))
          if (!groupMembers) return

          // GroupMember doesn't have id, match by email
          const groupUsers = groupMembers
            .map((member) => allUsers.find((u) => u.email === member.email))
            .filter(Boolean) as User[]
          result.push(...groupUsers)
        })
      )
    }
  }
  return result
}
